import {
  BoxGeometry,
  CapsuleGeometry,
  CylinderGeometry,
  DirectionalLight,
  Group,
  HemisphereLight,
  Mesh,
  MeshStandardMaterial,
  Object3D,
  PerspectiveCamera,
  Quaternion,
  Scene,
  Vector3,
} from 'three';
import { WebSide } from '../core/types';

const SUIT_RED = 0xe22b2b;
const SUIT_BLUE = 0x2244aa;
const FORWARD = new Vector3(0, 0, -1);

interface Hand {
  readonly arm: Group;
  readonly fist: Object3D;
  readonly rest: Vector3;
  readonly idleQuat: Quaternion;
  readonly anchor: Vector3;
  active: boolean;
  hasAnchor: boolean;
  reach: number; // 0 = relaxed, 1 = fully extended toward target
  missTimer: number; // brief reach animation when a shot misses
}

/**
 * The first-person arms. Lives in the HUD scene and is snapped onto the camera
 * each frame, so arm offsets are camera-relative and fist world positions are
 * exact. Animates a relaxed idle pose and a "thwip" reach that aims at the web
 * anchor.
 */
export class HandsRig {
  private readonly rig = new Group();
  private readonly hands: Record<WebSide, Hand>;
  private elapsed = 0;

  // scratch objects (avoid per-frame allocation)
  private readonly localTarget = new Vector3();
  private readonly aimDir = new Vector3();
  private readonly aimQuat = new Quaternion();
  private readonly forwardScratch = new Vector3();

  constructor(
    hudScene: Scene,
    private readonly camera: PerspectiveCamera,
  ) {
    hudScene.add(this.rig);
    hudScene.add(new HemisphereLight(0xffffff, 0x556677, 1.1));
    const dir = new DirectionalLight(0xffffff, 0.8);
    dir.position.set(0.5, 1, 1);
    hudScene.add(dir);

    this.hands = {
      L: this.createHand(-1, new Vector3(-0.3, -0.34, -0.22), new Vector3(0.42, 0.34, -1)),
      R: this.createHand(1, new Vector3(0.3, -0.34, -0.22), new Vector3(-0.42, 0.34, -1)),
    };
  }

  beginShoot(side: WebSide, anchorWorld: Vector3 | null): void {
    const hand = this.hands[side];
    if (anchorWorld) {
      hand.active = true;
      hand.hasAnchor = true;
      hand.anchor.copy(anchorWorld);
    } else {
      hand.active = false;
      hand.hasAnchor = false;
      hand.missTimer = 0.18;
    }
  }

  endShoot(side: WebSide): void {
    this.hands[side].active = false;
    this.hands[side].hasAnchor = false;
  }

  fistWorldPosition(side: WebSide, out: Vector3): Vector3 {
    const fist = this.hands[side].fist;
    fist.updateWorldMatrix(true, false);
    return fist.getWorldPosition(out);
  }

  update(dt: number): void {
    this.elapsed += dt;
    this.camera.updateMatrixWorld();
    this.rig.position.copy(this.camera.position);
    this.rig.quaternion.copy(this.camera.quaternion);

    for (const side of ['L', 'R'] as const) this.animate(this.hands[side], dt, side);
  }

  private animate(hand: Hand, dt: number, side: WebSide): void {
    if (hand.active) hand.reach = Math.min(1, hand.reach + dt * 8);
    else if (hand.missTimer > 0) {
      hand.missTimer -= dt;
      hand.reach = Math.min(1, hand.reach + dt * 10);
    } else {
      hand.reach = Math.max(0, hand.reach - dt * 7);
    }

    const sway = Math.sin(this.elapsed * 0.6 + (side === 'L' ? 0 : Math.PI)) * 0.012;
    const breathe = Math.sin(this.elapsed * 1.5 + (side === 'L' ? 0 : 1.5)) * 0.01;
    const rx = hand.rest.x;
    const ry = hand.rest.y + breathe;
    const rz = hand.rest.z + sway;

    const reaching = hand.reach > 0.001 && (hand.active || hand.missTimer > 0);
    if (reaching) {
      hand.arm.position.set(rx * (1 - hand.reach * 0.4), ry + hand.reach * 0.22, rz - hand.reach * 0.06);
      this.aimAt(hand);
    } else {
      hand.arm.position.set(rx, ry, rz);
      hand.arm.quaternion.slerp(hand.idleQuat, Math.min(1, dt * 10));
    }
  }

  /** Rotate the arm so its forward (-Z) axis points at the anchor (or straight
   *  ahead on a miss). */
  private aimAt(hand: Hand): void {
    if (hand.hasAnchor) {
      this.localTarget.copy(hand.anchor);
    } else {
      this.forwardScratch.set(0, 0, -1).applyQuaternion(this.camera.quaternion);
      this.localTarget.copy(this.camera.position).addScaledVector(this.forwardScratch, 30);
    }
    this.camera.worldToLocal(this.localTarget);
    this.aimDir.copy(this.localTarget).sub(hand.arm.position).normalize();
    this.aimQuat.setFromUnitVectors(FORWARD, this.aimDir);
    hand.arm.quaternion.slerp(this.aimQuat, 0.35);
  }

  /** Builds a suited arm extending along -Z, with a web-shooter fist. */
  private createHand(side: number, rest: Vector3, idleDir: Vector3): Hand {
    const arm = new Group();
    const red = new MeshStandardMaterial({ color: SUIT_RED, roughness: 0.55 });
    const blue = new MeshStandardMaterial({ color: SUIT_BLUE, roughness: 0.55 });

    const upper = new Mesh(new CapsuleGeometry(0.072, 0.24, 6, 10), red);
    upper.rotation.x = Math.PI / 2;
    upper.position.set(0, 0, -0.16);
    arm.add(upper);

    const forearm = new Group();
    forearm.position.set(0, 0, -0.3);
    const foreMesh = new Mesh(new CapsuleGeometry(0.062, 0.24, 6, 10), blue);
    foreMesh.rotation.x = Math.PI / 2;
    foreMesh.position.set(0, 0, -0.15);
    const cuff = new Mesh(new CylinderGeometry(0.08, 0.08, 0.05, 14), red);
    cuff.rotation.x = Math.PI / 2;
    cuff.position.set(0, 0, -0.27);
    forearm.add(foreMesh, cuff);

    const fist = new Group();
    fist.position.set(0, 0, -0.32);
    const palm = new Mesh(new BoxGeometry(0.15, 0.14, 0.14), red);
    palm.position.set(0, 0, -0.04);
    fist.add(palm);
    const shooter = new Mesh(
      new CylinderGeometry(0.045, 0.05, 0.08, 12),
      new MeshStandardMaterial({ color: 0x1a1a1a, roughness: 0.3, metalness: 0.7 }),
    );
    shooter.rotation.x = Math.PI / 2;
    shooter.position.set(0, 0, -0.11);
    fist.add(shooter);
    for (const fx of [-0.045, 0.045]) {
      const finger = new Mesh(new CapsuleGeometry(0.024, 0.12, 3, 6), red);
      finger.rotation.x = Math.PI / 2;
      finger.position.set(fx, 0.025, -0.13);
      fist.add(finger);
    }
    const thumb = new Mesh(new CapsuleGeometry(0.022, 0.07, 3, 6), red);
    thumb.rotation.set(Math.PI / 2, 0, side * 0.7);
    thumb.position.set(-side * 0.07, 0, -0.05);
    fist.add(thumb);

    forearm.add(fist);
    arm.add(forearm);

    const idleQuat = new Quaternion().setFromUnitVectors(FORWARD, idleDir.clone().normalize());
    arm.position.copy(rest);
    arm.quaternion.copy(idleQuat);
    this.rig.add(arm);

    return {
      arm,
      fist,
      rest,
      idleQuat,
      anchor: new Vector3(),
      active: false,
      hasAnchor: false,
      reach: 0,
      missTimer: 0,
    };
  }
}
